export const PUBLIC_SITE_URL = "https://www.demostoke.com";

const titleCaseToken = (token) => {
  if (!token) return "";
  if (/^\d+$/.test(token)) return token;
  if (/\d/.test(token) && /[a-z]/.test(token)) return token.toUpperCase();
  if (["ai", "aipa", "api", "bmx", "ci", "doa", "diy", "gps", "mtb", "usa"].includes(token)) {
    return token.toUpperCase();
  }
  if (["a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to", "vs"].includes(token)) {
    return token;
  }
  return `${token.charAt(0).toUpperCase()}${token.slice(1)}`;
};

export const humanizeSlug = (value) =>
  (value || "")
    .split("-")
    .filter(Boolean)
    .map((token, index) => {
      const formattedToken = titleCaseToken(token);
      if (index > 0) {
        return formattedToken;
      }

      return formattedToken.charAt(0).toUpperCase() + formattedToken.slice(1);
    })
    .join(" ")
    .replace(/\s+/g, " ")
    .trim();

export const extractGearNameFromSlug = (gearSlug) => {
  const slugWithoutId = (gearSlug || "").replace(/--[^/]+$/, "");
  const readableSlug = humanizeSlug(slugWithoutId);

  return readableSlug.replace(
    /(\b\d{1,2}) (\d{1,2}\b)(?!.*\b\d{1,2} \d{1,2}\b)/,
    "$1'$2",
  );
};

export const buildBlogPostTitle = (title) => `${title} | DemoStoke`;
export const buildGearDetailTitle = (displayName) => `${displayName} | DemoStoke`;
export const buildUserProfileTitle = (name) => `${name} | DemoStoke`;
export const buildDemoEventTitle = (title) => `${title} | Demo Event | DemoStoke`;

const sanitizeMetaText = (value) =>
  (value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/["]+/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const truncateMetaText = (value, maxLength = 160) => {
  if (value.length <= maxLength) {
    return value;
  }

  const truncated = value.slice(0, maxLength - 1).replace(/\s+\S*$/, "").trim();
  return `${truncated}...`;
};

const pexelsSquareThumb = (photoId) =>
  `https://images.pexels.com/photos/${photoId}/pexels-photo-${photoId}.jpeg?auto=compress&cs=tinysrgb&w=800&h=800&fit=crop`;

// Default thumbnail for every demo event in a category. Pexels photos are under
// the Pexels License (free, no attribution); the surfboards image comes from
// picryl (public domain) and is hosted in our gear-images bucket. The
// discover-demo-events edge function and migration 20261011150000 keep
// hand-copied URLs; keep all three in sync.
export const DEMO_EVENT_CATEGORY_IMAGES = {
  surfboards:
    "https://qtlhqsqanbxgfbcjigrl.supabase.co/storage/v1/object/public/gear-images/demo-event-thumbnails/surfboards-picryl-cc28fe.jpg",
  snowboards: pexelsSquareThumb(10808867),
  skis: pexelsSquareThumb(6783201),
  "mountain-bikes": pexelsSquareThumb(3880623),
};

export const getDemoEventCategoryImage = (gearCategory) =>
  DEMO_EVENT_CATEGORY_IMAGES[gearCategory] || null;

const DEMO_EVENT_CATEGORY_LABELS = {
  surfboards: "surfboard",
  snowboards: "snowboard",
  skis: "ski",
  "mountain-bikes": "mountain bike",
};

const formatMetaDate = (value) => {
  if (!value) return "";

  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return sanitizeMetaText(value);
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(parsed);
};

const formatMetaTime = (value) => {
  if (!value) return "";

  const [hours, minutes] = String(value).split(":");
  const parsed = new Date();
  parsed.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
  }).format(parsed);
};

export const buildUserProfileDescription = (name, detail = "") => {
  const baseDescription = `View the profile and listed gear from ${name} on DemoStoke.`;
  const sanitizedDetail = sanitizeMetaText(detail);

  if (!sanitizedDetail) {
    return baseDescription;
  }

  return truncateMetaText(`${baseDescription} ${sanitizedDetail}`);
};

export const buildDemoEventDescription = ({
  title,
  company,
  gearCategory,
  eventDate,
  eventTime,
  location,
  equipmentAvailable,
}) => {
  const normalizedCategory = DEMO_EVENT_CATEGORY_LABELS[gearCategory]
    || sanitizeMetaText(humanizeSlug(gearCategory || "gear")).toLowerCase();
  const leadText = sanitizeMetaText(title)
    ? `${sanitizeMetaText(title)} is a ${normalizedCategory} demo event on DemoStoke.`
    : `Find details for this ${normalizedCategory} demo event on DemoStoke.`;
  const detailParts = [];

  if (company) {
    detailParts.push(`Hosted by ${sanitizeMetaText(company)}`);
  }

  const formattedDate = formatMetaDate(eventDate);
  const formattedTime = formatMetaTime(eventTime);
  if (formattedDate && formattedTime) {
    detailParts.push(`Happening ${formattedDate} at ${formattedTime}`);
  } else if (formattedDate) {
    detailParts.push(`Happening ${formattedDate}`);
  } else if (formattedTime) {
    detailParts.push(`Starts at ${formattedTime}`);
  }

  if (location) {
    detailParts.push(`Location: ${sanitizeMetaText(location)}`);
  }

  if (equipmentAvailable) {
    detailParts.push(`Gear highlights: ${sanitizeMetaText(equipmentAvailable)}`);
  }

  return truncateMetaText(`${leadText} ${detailParts.join(". ")}.`.replace(/\.\s+\./g, "."));
};

export const PUBLIC_ROUTE_META = {
  "/": {
    title: "Demo & Rent Surfboards From Local Shops | DemoStoke",
    description:
      "DemoStoke is the marketplace to demo, rent, and try surfboards from local surf shops, indie shapers, and fellow surfers. Snowboards, skis, and mountain bikes too. Try before you buy.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/`,
  },
  "/about": {
    title: "About DemoStoke | Action Sports Gear Demo Marketplace",
    description:
      "DemoStoke connects riders with local shops, indie shapers, and gear owners for surfboard, snowboard, ski, and mountain bike demos and rentals. Built by riders, for riders.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/about`,
  },
  "/explore": {
    title: "Explore Surfboards & Gear Demos Near You | DemoStoke",
    description:
      "Browse surfboards available for demo and rental near you, plus snowboards, skis, and mountain bikes. Filter by sport, location, skill level, and price.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/explore`,
  },
  "/how-it-works": {
    title: "How DemoStoke Works | Try Before You Buy Gear Rentals",
    description:
      "Browse gear on the DemoStoke map, connect with local shops or owners, try the gear in real conditions, and buy what you love. No lines, no outdated rentals.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/how-it-works`,
  },
  "/blog": {
    title: "Surfboard Reviews, Gear Guides & Surf Stories | DemoStoke Blog",
    description:
      "Surfboard reviews, gear guides, snow and bike reviews, demo day calendars, and rider stories from the DemoStoke community.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/blog`,
  },
  "/contact-us": {
    title: "Contact DemoStoke | Questions, Listings & Support",
    description:
      "Have questions about DemoStoke? Want to list your shop or gear? Reach out to the DemoStoke team.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/contact-us`,
  },
  "/list-your-gear": {
    title: "List Your Gear or Shop on DemoStoke | Partner With Us",
    description:
      "List your surf shop, ski rental, or personal gear on DemoStoke. Get discovered by riders looking for demos and rentals in your area. Free to start.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/list-your-gear`,
  },
  "/search": {
    title: "Search Surfboards, Snowboards, Skis & Bikes | DemoStoke",
    description:
      "Search surfboards available for demo and rental on DemoStoke, along with snowboards, skis, and mountain bikes.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/search`,
  },
  "/gear": {
    title: "Action Sports Gear Index | DemoStoke",
    description:
      "DemoStoke indexes real-world rental, demo, and used action sports gear with model details, location context, and freshness timestamps.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/gear`,
  },
  "/gear/surfboards": {
    title: "Surfboard Demos & Rentals | DemoStoke",
    description:
      "Browse surfboards available for demo and rental from local surf shops and riders. Shortboards, longboards, fish, mid-lengths, and more.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/gear/surfboards`,
  },
  "/gear/used-skis": {
    title: "Used Ski Rentals & Demos | DemoStoke",
    description:
      "Browse used skis available for rental and demo with current availability, location, and pricing.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/gear/used-skis`,
  },
  "/demo-calendar": {
    title: "Demo Day Calendar | Upcoming Gear Demo Events | DemoStoke",
    description:
      "Find upcoming surfboard, snowboard, and ski demo days near you. Try new gear from top brands at local shops and events.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/demo-calendar`,
  },
  "/gear-quiz": {
    title: "Gear Quiz | Find Your Perfect Board | DemoStoke",
    description:
      "Answer a few questions about your riding style, skill level, and conditions to get personalized gear recommendations from DemoStoke.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/gear-quiz`,
  },
  "/privacy-policy": {
    title: "Privacy Policy | DemoStoke",
    description: "DemoStoke privacy policy. How we collect, use, and protect your data.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/privacy-policy`,
  },
  "/terms-of-service": {
    title: "Terms of Service | DemoStoke",
    description: "DemoStoke terms of service for riders, gear owners, and shop partners.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/terms-of-service`,
  },
  "/user-profile/chad-g": {
    title: "Chad G. | AI Author at DemoStoke",
    description: "About Chad G., AI author at DemoStoke.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/user-profile/chad-g`,
  },
  "/user-profile/gemini": {
    title: "Gemini | AI Author at DemoStoke",
    description: "About Gemini, AI author at DemoStoke.",
    type: "website",
    canonicalUrl: `${PUBLIC_SITE_URL}/user-profile/gemini`,
  },
};

export const getPublicRouteMeta = (path) => PUBLIC_ROUTE_META[path];
